/**
 * @component ry-drawer
 * @category  overlay
 * @summary   Slide-out side panel (Fomantic UI `sidebar` / `flyout` equivalent). Anchors to one of four edges; opens with a transform, traps focus, locks body scroll, closes on Escape + backdrop click. Declarative trigger via `drawer="id"` on `<ry-button>`.
 *
 * Reference: Fomantic UI's `.ui.sidebar` and `.ui.flyout`. Side maps to
 * Fomantic's direction (`.left.sidebar`, etc.); size maps to thin/wide/very-wide.
 * The transition is always a slide (overlay) — Fomantic's "push", "uncover",
 * "scale down" transitions are intentionally not modelled.
 *
 * @attribute id          string?            Used by `<ry-button drawer="id">` triggers. Required for declarative pairing.
 * @attribute side        'left' | 'right' | 'top' | 'bottom'   default="left"   Which edge the drawer slides from.
 * @attribute size        'thin' | 'wide' | 'very-wide' ? default=(unset: 20rem)   Drawer thickness. Maps to width for left/right, height for top/bottom.
 * @attribute open        boolean   default=false   When present at mount, the drawer starts opened (rare — most drawers open on user action).
 * @attribute persistent  boolean   default=false   Escape and backdrop-click do NOT close. Only `el.close()` or an inner `[close]` button closes it. Use for required-flow drawers.
 *
 * @property  state       'open' | 'closed'    Mirrors `data-ry-state`.
 *
 * @method    open()      Open the drawer; locks body scroll; traps focus; fires `ry:open`. No-op if already open.
 * @method    close()     Close the drawer; releases scroll lock; restores focus; fires `ry:close`. No-op if already closed.
 * @method    toggle()    Open if closed, close if open.
 *
 * @event ry:beforeopen
 *        detail: {}
 *        fires:  at the top of `open()` — trigger click or `el.open()` — before scroll lock, focus capture, or any state change
 *        cancelable: preventDefault() keeps the drawer closed; `ry:open` does not fire
 *
 * @event ry:open
 *        detail: {}
 *        fires:  after `open()` transitions state to "open"
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of `close()` — Escape, backdrop click, `[close]` trigger, or `el.close()` — before any state change
 *        cancelable: preventDefault() keeps the drawer open; `ry:close` does not fire. The unsaved-changes guard.
 *
 * @event ry:close
 *        detail: {}
 *        fires:  after `close()` transitions state to "closed"

 *
 * @keyboard  Escape — closes (unless `[persistent]`). Tab/Shift+Tab cycle focus inside the panel only.
 *
 * @state     open / closed — `data-ry-state` drives the slide transition via CSS `@starting-style`.
 *
 * @content   All children become the drawer body. A `[close]` attribute on any descendant button wires it to call `close()`.
 *
 * @dom
 *   ry-drawer[data-ry-state="open"|"closed"][data-ry-side="left"|"right"|"top"|"bottom"]
 *     > div.ry-drawer__backdrop    [data-ry-target="backdrop"]
 *     > div.ry-drawer__panel       [data-ry-target="panel"]  role="dialog" aria-modal="true"
 *       > button.ry-drawer__close  [data-ry-target="close"] [close]
 *       > div.ry-drawer__content   [data-ry-target="content"]
 *
 * @gotcha  Body scroll lock is shared with `<ry-modal>` (same `data-ry-scroll-lock` attribute). Open both at once and closing one releases the lock for the other — by design, only one overlay open at a time.
 *
 * @gotcha  The trigger `drawer="id"` attribute lives on `<ry-button>` (or any element), not on `<ry-drawer>` itself. Triggers are scanned at the drawer's setup time — add triggers BEFORE the drawer mounts, or call `drawer.open()` directly.
 *
 * @gotcha  `[persistent]` blocks Escape AND backdrop click. It does NOT block `el.close()` or `[close]`-attributed children — those always work.
 *
 * @example default-left
 * ```html
 * <ry-button drawer="menu">Open</ry-button>
 * <ry-drawer id="menu"><nav>Menu items</nav></ry-drawer>
 * ```
 *
 * @example right
 * ```html
 * <ry-button drawer="cart">Open right</ry-button>
 * <ry-drawer id="cart" side="right"><h3>Cart</h3><p>0 items</p><ry-button close>Close</ry-button></ry-drawer>
 * ```
 *
 * @example top
 * ```html
 * <ry-button drawer="notice-top">Open top</ry-button>
 * <ry-drawer id="notice-top" side="top"><p>System notice.</p><ry-button close>Close</ry-button></ry-drawer>
 * ```
 *
 * @example bottom
 * ```html
 * <ry-button drawer="picker-bottom">Open bottom</ry-button>
 * <ry-drawer id="picker-bottom" side="bottom"><p>Picker UI</p><ry-button close>Close</ry-button></ry-drawer>
 * ```
 *
 * @example thin
 * ```html
 * <ry-button drawer="icons-thin">Open thin</ry-button>
 * <ry-drawer id="icons-thin" side="left" size="thin"><nav>Icon rail</nav><ry-button close>Close</ry-button></ry-drawer>
 * ```
 *
 * @example wide
 * ```html
 * <ry-button drawer="inspector-wide">Open wide</ry-button>
 * <ry-drawer id="inspector-wide" side="right" size="wide"><h3>Inspector</h3><ry-button close>Close</ry-button></ry-drawer>
 * ```
 *
 * @example very-wide
 * ```html
 * <ry-button drawer="settings-vw">Open very-wide</ry-button>
 * <ry-drawer id="settings-vw" side="right" size="very-wide"><h3>Settings</h3><ry-button close>Close</ry-button></ry-drawer>
 * ```
 *
 * @example persistent
 * ```html
 * <ry-button drawer="must-agree">Open persistent</ry-button>
 * <ry-drawer id="must-agree" side="right" persistent>
 *   <p>Accept terms to continue. Escape and backdrop click are blocked.</p>
 *   <ry-button variant="primary" close>I accept</ry-button>
 * </ry-drawer>
 * ```
 *
 * @tutorial open-and-close
 * ```js
 * const el = mount('<ry-drawer id="t"><nav>x</nav></ry-drawer>');
 * await expectEqual(el.state, 'closed', 'starts closed');
 * await expectEvent(el, 'ry:open', () => el.open());
 * await expectEqual(el.state, 'open');
 * await expectEvent(el, 'ry:close', () => el.close());
 * await expectEqual(el.state, 'closed');
 * ```
 *
 * @tutorial escape-closes
 * ```js
 * const el = mount('<ry-drawer id="t"><nav>x</nav></ry-drawer>');
 * el.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'closed', 'escape closes the drawer');
 * ```
 *
 * @tutorial backdrop-click-closes
 * ```js
 * const el = mount('<ry-drawer id="t"><nav>x</nav></ry-drawer>');
 * el.open();
 * el.querySelector('[data-ry-target="backdrop"]').click();
 * await expectEqual(el.state, 'closed', 'backdrop click closes');
 * ```
 *
 * @tutorial side-reflects-to-panel
 * ```js
 * // Wrong assumption: side="right" only affects CSS. It also sets
 * // data-ry-side on the panel so the slide transform animates from the
 * // right edge. Confirming the attribute proves the CSS hook is in place.
 * const el = mount('<ry-drawer id="t" side="right"><nav>x</nav></ry-drawer>');
 * await expectAttr(el.querySelector('[data-ry-target="panel"]'), 'data-ry-side', 'right');
 * ```
 *
 * @tutorial persistent-blocks-escape-and-backdrop
 * ```js
 * // Wrong assumption: every drawer closes on Escape. [persistent] drawers do not.
 * const el = mount('<ry-drawer id="t" persistent><nav>x</nav></ry-drawer>');
 * el.open();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'open', 'escape ignored when persistent');
 * el.querySelector('[data-ry-target="backdrop"]').click();
 * await expectEqual(el.state, 'open', 'backdrop click ignored when persistent');
 * el.close();
 * await expectEqual(el.state, 'closed', 'el.close() still works on persistent');
 * ```
 *
 * @tutorial inner-close-attribute
 * ```js
 * const el = mount('<ry-drawer id="t"><button id="ok" close>OK</button></ry-drawer>');
 * el.open();
 * el.querySelector('#ok').click();
 * await expectEqual(el.state, 'closed', 'inner [close] button closes');
 * ```
 *
 * @tutorial open-attribute-starts-open
 * ```js
 * // Wrong assumption: [open] is observed on attribute change. It is only
 * // honored at setup() time — once mounted, removing/adding the attribute
 * // does not open/close. Use el.open() / el.close() for runtime control.
 * const el = mount('<ry-drawer id="t" open><nav>x</nav></ry-drawer>');
 * await expectEqual(el.state, 'open', '[open] auto-opens at mount');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // The unsaved-changes guard: preventDefault() on ry:beforeclose keeps the
 * // drawer open, whatever asked it to close (Escape, backdrop, el.close()).
 * const el = mount('<ry-drawer id="t"><nav>x</nav></ry-drawer>');
 * el.open();
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.state, 'open', 'vetoed close leaves state open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen keeps the drawer closed — no scroll
 * // lock, no focus trap, no ry:open.
 * const el = mount('<ry-drawer id="t"><nav>x</nav></ry-drawer>');
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * el.open();
 * await expectEqual(el.state, 'closed', 'vetoed open leaves state closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * el.open();
 * await expectEqual(el.state, 'open', 'without the veto, open() works');
 * ```
 *
 * @chain ry-button
 * ```js
 * // The declarative pairing — <ry-button drawer="x"> opens <ry-drawer id="x">.

 * // mount() only appends the first root element, so wrap in <div>.
 * const root = mount(`
 *   <div>
 *     <ry-button drawer="demo" id="trigger">Open</ry-button>
 *     <ry-drawer id="demo" side="left"><nav>Menu</nav></ry-drawer>
 *   </div>
 * `);
 * const trigger = root.querySelector('#trigger');
 * const drawer = root.querySelector('#demo');
 * await expectEqual(drawer.state, 'closed');
 * trigger.click();
 * await expectEqual(drawer.state, 'open', 'trigger opens paired drawer');
 * ```
 *
 * @see ry-button
 * @see ry-modal
 */

import { RyElement } from '../core/ry-element.js';

export class RyDrawer extends RyElement {
  #scrollbarWidth = 0;
  #restoreFocus: (() => void) | null = null;

  setup(): void {
    this.#transformMarkup();

    // Find trigger buttons anywhere in document
    const drawerId = this.id;
    if (drawerId) {
      // drawer="id" attribute on ry-button
      document.querySelectorAll<HTMLElement>(`ry-button[drawer="${drawerId}"], [drawer="${drawerId}"]`).forEach(trigger => {
        this.on(trigger, 'click', () => this.open());
      });
    }

    // Close buttons inside drawer (use [close] attribute)
    this.$$('[close]').forEach(closeBtn => {
      this.on(closeBtn, 'click', () => this.close());
    });

    // Backdrop click (skipped when [persistent])
    const backdrop = this.$('[data-ry-target="backdrop"]');
    if (backdrop) {
      this.on(backdrop, 'click', () => {
        if (this.hasAttribute('persistent')) return;
        this.close();
      });
    }

    // Escape key
    this.on(document, 'keydown', this.#handleKeydown);

    // Calculate scrollbar width
    this.#scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Initialize state
    if (this.hasAttribute('open')) {
      this.state = 'open';
    } else if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }
  }

  #transformMarkup(): void {
    // Check if already has full markup
    if (this.$('[data-ry-target="panel"]')) return;

    const side = this.getAttribute('side') ?? 'left';
    const content = this.innerHTML;

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <div data-ry-target="backdrop" class="ry-drawer__backdrop"></div>
      <div data-ry-target="panel" class="ry-drawer__panel" role="dialog" aria-modal="true" data-ry-side="${side}">
        <button data-ry-target="close" class="ry-drawer__close" close aria-label="Close">&times;</button>
        <div data-ry-target="content" class="ry-drawer__content">
          ${content}
        </div>
      </div>
    `;

    // Set side attribute for CSS
    this.setAttribute('data-ry-side', side);
  }

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (this.state !== 'open') return;
    if (this.hasAttribute('persistent')) return;
    this.close();
  };

  open(): void {
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) return;

    // Capture focus to restore on close — shared helper

    this.#restoreFocus = this.withReturnFocus();

    // Lock body scroll — REFERENCE-COUNTED. Nested overlays don't clobber.
    this.lockScroll();
    document.body.style.setProperty('--ry-scrollbar-width', `${this.#scrollbarWidth}px`);

    // Open drawer
    this.state = 'open';

    // Trap focus
    const panel = this.$<HTMLElement>('[data-ry-target="panel"]');
    if (panel) {
      this.nextFrame().then(() => {
        this.trapFocus(panel);
      });
    }

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;

    this.unlockScroll();

    if (!document.body.hasAttribute('data-ry-scroll-lock')) {
      document.body.style.removeProperty('--ry-scrollbar-width');
    }

    this.state = 'closed';

    this.#restoreFocus?.();
    this.#restoreFocus = null;

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }
}

customElements.define('ry-drawer', RyDrawer);
